import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from 'react';
import Tabs from '../components/common/Tab/Tabs';
import SearchArea from '../components/InputEdit/SearchArea/SearchArea';
import Content from '../components/InputEdit/Content/Content';
import Footer from '../components/InputEdit/Footer/Footer';
import type { ImageAddCandidate } from '../components/common/ImageAddDialog/ImageAddDialog';
import type { ImageAttachment } from '../types/imageAttachment';
import { postPresignedDownloadUrls } from '../api/imageDownload';
import Snackbar from '@mui/material/Snackbar';
import { SnackbarCloseReason } from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  MAX_BODY_LENGTH,
  useDivisionOptions,
  useTeamOptions,
} from '../state/master';
import useLocalStorage from '../components/InputEdit/Content/useLocalStorage';
import type { HistoryItem } from '../components/InputEdit/OverwriteModal/OverwriteModal';
import type { SearchSelectionPayload } from '../components/InputEdit/SearchArea/OverwriteInputSearchArea';
import { useUserProfile } from '../context/UserProfileContext';
// WebSocket 関連
import { useAppWebSocket } from '../context/WebSocketContext';
import { ReadyState } from 'react-use-websocket';
import { useUser } from '../context/UserContext';
import { useUserName } from '../context/UserNameContext';
import { useLoginId } from '../hooks/useLoginId';
import {
  sendStatusUsing,
  sendStatusRelease,
  WeeklyReportStatus,
} from '../components/WebSocket/statusMessage';
import { apiClient, isApiError } from '../lib/apiClient';
import {
  parseEnlightSyncDefault,
  readRemandEnlightSyncPreference,
  resolveOverwriteEnlightSyncEnabled,
} from '../components/InputEdit/enlightSync';
import { missingRequiredDynamicTypes } from '../components/InputEdit/requiredDynamicFields';

interface ValidationErrors {
  reporter?: string;
  startDate?: string;
  title?: string;
  body?: string;
  dynamic?: string;
}

const sameValidationErrors = (a: ValidationErrors, b: ValidationErrors): boolean =>
  a.reporter === b.reporter &&
  a.startDate === b.startDate &&
  a.title === b.title &&
  a.body === b.body &&
  a.dynamic === b.dynamic;

const readLocalStorageString = (key: string): string => {
  if (typeof window === 'undefined') return '';

  try {
    const raw = window.localStorage.getItem(key);
    if (raw == null) return '';
    const parsed = JSON.parse(raw);
    return typeof parsed === 'string' ? parsed : String(parsed ?? '');
  } catch {
    return '';
  }
};

const writeLocalStorageString = (key: string, value: string): void => {
  if (typeof window === 'undefined') return;

  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // localStorage failures should not break typing.
  }
};

const removeLocalStorageValue = (key: string): void => {
  if (typeof window === 'undefined') return;

  try {
    window.localStorage.removeItem(key);
  } catch {
    // localStorage failures should not break typing.
  }
};

type OverwriteFileRecord = {
  file_id?: number;
  fileId?: number;
  object_key?: string;
  objectKey?: string;
  object_key_hash?: string;
  objectKeyHash?: string;
  original_name?: string;
  originalName?: string;
  signed_url?: string;
  signedUrl?: string;
  url?: string;
  public_url?: string;
  publicUrl?: string;
  status?: string;
  [key: string]: any;
};

// ─────────────────────────────────────────────
// 他ユーザー編集中の表示バー（共通コンポーネント）
// ─────────────────────────────────────────────
interface LockWarningBarProps {
  isLocked: boolean;
  lockedByName: string | null;
}

export const LockWarningBar: React.FC<LockWarningBarProps> = ({
  isLocked,
  lockedByName,
}) => {
  if (!isLocked) {
    return null;
  }

  const message = lockedByName
    ? `${lockedByName} さんが編集中のため、この画面は閲覧のみ可能です。`
    : '別のユーザーが編集中のため、この画面は閲覧のみ可能です。';

  return (
    <div
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 1300,
        width: '100%',
        backgroundColor: '#fff3cd',
        color: '#856404',
        padding: '8px 16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 14,
        borderBottom: '1px solid #ffeeba',
      }}
    >
      {message}
    </div>
  );
};

const InputEdit: React.FC = () => {
  const { divisionOptions } = useDivisionOptions();
  const { teamOptions } = useTeamOptions();

  const user = useUser();

  // ログインID（email）ベースで「自分自身」を判定するために保持
  // Hosted UI ログインでは signInDetails が無いため、email へフォールバックする
  const selfLoginId = useLoginId();

  // UserProfile → 氏名初期表示
  const { profile } = useUserProfile();
  const profileFullName = useMemo(() => {
    const ln = String(profile?.lastName ?? '').trim();
    const fn = String(profile?.firstName ?? '').trim();
    return [ln, fn].filter(Boolean).join(' ');
  }, [profile?.lastName, profile?.firstName]);

  const draftStorageUserKey = useMemo(() => {
    const attrs = (profile as any)?.cognitoAttributes ?? {};
    const candidates = [
      selfLoginId,
      attrs?.email,
      attrs?.preferred_username,
      attrs?.username,
      attrs?.sub,
    ];

    return (
      candidates
        .map((v) => String(v ?? '').trim().toLowerCase())
        .find((v) => v.length > 0) ?? ''
    );
  }, [selfLoginId, profile]);
  const draftStorageReady = draftStorageUserKey.length > 0;
  const draftStorageSuffix = draftStorageReady ? draftStorageUserKey : '__pending__';

  // ─────────────────────────────────────────────
  // Newタブの「課 / チーム」は、画面遷移・モーダル遷移・Overwrite往復でも維持したい
  // → localStorage に保存してキャッシュする（ユーザー別キー）
  // ─────────────────────────────────────────────
  const [
    selectedDivisionNew,
    setSelectedDivisionNew,
  ] = useLocalStorage<string>(
    `inputEdit:selectedDivision:new:${selfLoginId || 'anon'}`,
    '',
    true
  );

  const [
    teamNew,
    setTeamNew,
  ] = useLocalStorage<string>(
    `inputEdit:team:new:${selfLoginId || 'anon'}`,
    '',
    true
  );

  // Overwrite 側は New の値を壊さないために分離（必要なら同様に localStorage 化できます）
  const [selectedDivisionOverwrite, setSelectedDivisionOverwrite] =
    useState<string>('');
  const [teamOverwrite, setTeamOverwrite] = useState<string>('');

  const [activeTab, setActiveTab] = useState<'new' | 'overwrite'>('new');
  const isOverwrite: boolean = activeTab === 'overwrite';
  const [newFormDefaultResetSeq, setNewFormDefaultResetSeq] = useState(0);

  // ユーザーが変わった時だけ remount（または key 自体を消す）
  const searchAreaMountKey = useMemo(() => {
    const uid = selfLoginId || 'anon';
    return `SearchArea:${uid}`;
  }, [selfLoginId]);

  // タブ切替：overwrite に入る瞬間に new の課/チームを引き継ぐ
  const handleTabChange = useCallback(
    (nextTab: 'new' | 'overwrite' | 'sectionName' | null) => {
      // SearchArea 側の都合で null / sectionName が来る可能性があるのでガード
      if (nextTab !== 'new' && nextTab !== 'overwrite') return;

      setActiveTab(nextTab);

      if (nextTab === 'overwrite') {
        setSelectedDivisionOverwrite((prev) => prev || selectedDivisionNew);
        setTeamOverwrite((prev) => prev || teamNew);
      }
    },
    [selectedDivisionNew, teamNew, setActiveTab]
  );

  // InputEdit 全体で使う値/セッターは activeTab に応じて切り替える
  const selectedDivision = isOverwrite
    ? selectedDivisionOverwrite
    : selectedDivisionNew;

  const setSelectedDivision = useCallback(
    (next: React.SetStateAction<string>) => {
      if (isOverwrite) {
        const value =
          typeof next === 'function'
            ? (next as any)(selectedDivisionOverwrite)
            : next;
        setSelectedDivisionOverwrite(value);
        return;
      }

      const value =
        typeof next === 'function' ? (next as any)(selectedDivisionNew) : next;
      setSelectedDivisionNew(value);
    },
    [
      isOverwrite,
      selectedDivisionOverwrite,
      selectedDivisionNew,
      setSelectedDivisionNew,
    ]
  );

  useEffect(() => {
    // if (!selectedDivision && divisionOptions.length > 0) {
    //   setSelectedDivision(String(divisionOptions[0].value ?? divisionOptions[0].label ?? ''));
    // }
  }, [divisionOptions, selectedDivision, setSelectedDivision]);

  // NEW/OVERWRITE で日付を分離
  const [startDateNew, setStartDateNew] = useState<string>('');
  const [endDateNew, setEndDateNew] = useState<string>('');
  const [startDateOverwrite, setStartDateOverwrite] = useState<string>('');
  const [endDateOverwrite, setEndDateOverwrite] = useState<string>('');

  // startDate/endDate を “切替ビュー” にする
  const startDate = isOverwrite ? startDateOverwrite : startDateNew;
  const endDate = isOverwrite ? endDateOverwrite : endDateNew;

  const setStartDate = useCallback(
    (next: React.SetStateAction<string>) => {
      if (isOverwrite) {
        const value =
          typeof next === 'function'
            ? (next as any)(startDateOverwrite)
            : next;
        setStartDateOverwrite(value);
        return;
      }

      const value =
        typeof next === 'function' ? (next as any)(startDateNew) : next;
      setStartDateNew(value);
    },
    [isOverwrite, startDateOverwrite, startDateNew]
  );

  const setEndDate = useCallback(
    (next: React.SetStateAction<string>) => {
      if (isOverwrite) {
        const value =
          typeof next === 'function'
            ? (next as any)(endDateOverwrite)
            : next;
        setEndDateOverwrite(value);
        return;
      }

      const value =
        typeof next === 'function' ? (next as any)(endDateNew) : next;
      setEndDateNew(value);
    },
    [isOverwrite, endDateOverwrite, endDateNew]
  );



  const [wrId, setWrID] = useState<number>();
  const [reporter, setReporter] = useState<string>('');
  useEffect(() => {
    if (!reporter && profileFullName) setReporter(profileFullName);
  }, [profileFullName, reporter]);

  // 静的セレクト
  const team = isOverwrite ? teamOverwrite : teamNew;

  const setTeam = useCallback(
    (next: React.SetStateAction<string>) => {
      if (isOverwrite) {
        const value =
          typeof next === 'function' ? (next as any)(teamOverwrite) : next;
        setTeamOverwrite(value);
        return;
      }

      const value = typeof next === 'function' ? (next as any)(teamNew) : next;
      setTeamNew(value);
    },
    [isOverwrite, teamOverwrite, teamNew, setTeamNew]
  );

  const [reportType, setReportType] = useState<string>('');
  const [approver3, setApprover3] = useState<string>('');
  const [approverAM, setApproverAM] = useState<string>('');
  const [shikimuke, setShikimuke] = useState<string>('');
  const [lot, setLot] = useState<string>('');

  // Overwrite
  const [title, setTitle] = useState<string>('');
  const [body, setBody] = useState<string>('');
  const [fbNote, setFbNote] = useState<string>('');
  const [overwriteTeamId, setOverwriteTeamId] = useState<number | null>(null);
  const [temporaryFlg, setTemporaryFlg] = useState<number | null>(null);

  // スイッチ
  // 初期値を false に変更（マスタの enlight_sync_default から後で反映）
  const [enlightSyncEnabled, setEnlightSyncEnabled] =
    useState<boolean>(false);
  const [autoSaveEnabled, setAutoSaveEnabled] = useState<boolean>(true);

  const [validationErrors, setValidationErrors] = useState<ValidationErrors>(
    {}
  );
  const validationErrorsRef = useRef<ValidationErrors>({});

  useEffect(() => {
    validationErrorsRef.current = validationErrors;
  }, [validationErrors]);

  const setValidationErrorsIfChanged = useCallback((next: ValidationErrors) => {
    if (sameValidationErrors(validationErrorsRef.current, next)) return;
    validationErrorsRef.current = next;
    setValidationErrors(next);
  }, []);

  // New 下書き
  const draftTitleKey = useMemo(
    () => `draftTitle:${draftStorageSuffix}`,
    [draftStorageSuffix]
  );
  const draftBodyKey = useMemo(
    () => `draftBody:${draftStorageSuffix}`,
    [draftStorageSuffix]
  );
  const [draftTitle, setDraftTitleState] = useState<string>(() =>
    draftStorageReady ? readLocalStorageString(draftTitleKey) : ''
  );
  const [draftBody, setDraftBodyState] = useState<string>(() =>
    draftStorageReady ? readLocalStorageString(draftBodyKey) : ''
  );
  const draftTitleRef = useRef(draftTitle);
  const draftBodyRef = useRef(draftBody);

  useEffect(() => {
    const next = draftStorageReady ? readLocalStorageString(draftTitleKey) : '';
    if (draftTitleRef.current === next) return;
    draftTitleRef.current = next;
    setDraftTitleState(next);
  }, [draftStorageReady, draftTitleKey]);

  useEffect(() => {
    const next = draftStorageReady ? readLocalStorageString(draftBodyKey) : '';
    if (draftBodyRef.current === next) return;
    draftBodyRef.current = next;
    setDraftBodyState(next);
  }, [draftStorageReady, draftBodyKey]);

  useEffect(() => {
    draftTitleRef.current = draftTitle;
    if (!draftStorageReady) return;
    writeLocalStorageString(draftTitleKey, draftTitle);
  }, [draftStorageReady, draftTitleKey, draftTitle]);

  useEffect(() => {
    draftBodyRef.current = draftBody;
    if (!draftStorageReady) return;
    writeLocalStorageString(draftBodyKey, draftBody);
  }, [draftStorageReady, draftBodyKey, draftBody]);

  const setDraftTitle = useCallback((value: string) => {
    if (draftTitleRef.current === value) return;
    draftTitleRef.current = value;
    setDraftTitleState(value);
  }, []);

  const setDraftBody = useCallback((value: string) => {
    if (draftBodyRef.current === value) return;
    draftBodyRef.current = value;
    setDraftBodyState(value);
  }, []);

  const resetDraftTitle = useCallback(() => {
    if (draftTitleRef.current !== '') {
      draftTitleRef.current = '';
      setDraftTitleState('');
    }
    if (draftStorageReady) {
      writeLocalStorageString(draftTitleKey, '');
    } else {
      removeLocalStorageValue(draftTitleKey);
    }
  }, [draftStorageReady, draftTitleKey]);

  const resetDraftBody = useCallback(() => {
    if (draftBodyRef.current !== '') {
      draftBodyRef.current = '';
      setDraftBodyState('');
    }
    if (draftStorageReady) {
      writeLocalStorageString(draftBodyKey, '');
    } else {
      removeLocalStorageValue(draftBodyKey);
    }
  }, [draftStorageReady, draftBodyKey]);

  const [newImages, setNewImages] = useState<ImageAttachment[]>([]);
  const [overwriteImages, setOverwriteImages] = useState<ImageAttachment[]>([]);
  // 保存済み添付を削除したか（全削除で images が 0 件でも保存APIを呼ぶ必要があるため）
  const [overwriteSavedImageRemoved, setOverwriteSavedImageRemoved] = useState<boolean>(false);
  const overwriteFilesRequestIdRef = useRef(0);
  const newImagesRef = useRef<ImageAttachment[]>([]);
  const overwriteImagesRef = useRef<ImageAttachment[]>([]);

  const revokeImageAttachmentPreview = useCallback((img: ImageAttachment) => {
    if (img.kind !== 'file') return;
    try {
      URL.revokeObjectURL(img.previewUrl);
    } catch {
      // noop
    }
  }, []);

  useEffect(() => {
    newImagesRef.current = newImages;
  }, [newImages]);

  useEffect(() => {
    overwriteImagesRef.current = overwriteImages;
  }, [overwriteImages]);

  useEffect(() => {
    return () => {
      newImagesRef.current.forEach(revokeImageAttachmentPreview);
      overwriteImagesRef.current.forEach(revokeImageAttachmentPreview);
    };
  }, [revokeImageAttachmentPreview]);

  const makeImageAttachment = useCallback((candidate: ImageAddCandidate): ImageAttachment => {
    const id =
      typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? (crypto as Crypto).randomUUID()
        : `img-${Date.now()}-${Math.random().toString(36).slice(2)}`;

    if (candidate.type === 'file') {
      const file = candidate.file;
      return {
        id,
        kind: 'file',
        file,
        previewUrl: URL.createObjectURL(file),
        name: file.name || `image-${id}`,
      };
    }

    return {
      id,
      kind: 'url',
      url: candidate.url,
      previewUrl: candidate.url,
      name: candidate.url,
    };
  }, []);

  const resolveSignedImageUrl = useCallback((file: OverwriteFileRecord): string => {
    return (
      file.signed_url ??
      file.signedUrl ??
      file.url ??
      file.public_url ??
      file.publicUrl ??
      ''
    );
  }, []);

  const makeImageAttachmentFromFileRecord = useCallback(
    (file: OverwriteFileRecord, index: number, urlOverride?: string): ImageAttachment => {
      const idBase =
        file.file_id ??
        file.fileId ??
        file.object_key_hash ??
        file.objectKeyHash ??
        file.object_key ??
        file.objectKey ??
        index;
      const id = `file-${String(idBase)}`;
      const name =
        String(
          file.original_name ??
            file.originalName ??
            file.object_key ??
            file.objectKey ??
            `image-${idBase}`
        );
      const url =
        urlOverride ||
        resolveSignedImageUrl(file) ||
        file.object_key ||
        file.objectKey ||
        '';
      return {
        id,
        kind: 'url',
        url,
        previewUrl: url,
        name,
        file_id: file.file_id ?? file.fileId ?? null,
      };
    },
    [resolveSignedImageUrl]
  );

  const extractFileKey = useCallback((file: OverwriteFileRecord): string => {
    return String(file.object_key ?? file.objectKey ?? '').trim();
  }, []);

  const shouldIncludeFileRecord = useCallback((file: OverwriteFileRecord): boolean => {
    const status = String(file.status ?? '').trim().toUpperCase();
    if (!status) return true;
    return status === 'Y';
  }, []);

  const buildOverwriteImageAttachments = useCallback(
    async (files: OverwriteFileRecord[]): Promise<ImageAttachment[]> => {
      const usable = files.filter(shouldIncludeFileRecord);
      if (usable.length === 0) return [];

      const keys = Array.from(
        new Set(usable.map(extractFileKey).filter((key) => key.length > 0))
      );

      const signedUrlMap = new Map<string, string>();
      if (keys.length > 0) {
        try {
          const items = await postPresignedDownloadUrls(keys);
          items.forEach((item) => {
            if (item?.key && item?.url) {
              signedUrlMap.set(String(item.key), String(item.url));
            }
          });
        } catch (e) {
          console.error('Failed to fetch presigned download URLs', e);
        }
      }

      return usable
        .map((file, index) => {
          const key = extractFileKey(file);
          const url = key ? signedUrlMap.get(key) : undefined;
          const attachment = makeImageAttachmentFromFileRecord(file, index, url);
          return attachment.previewUrl ? attachment : null;
        })
        .filter(Boolean) as ImageAttachment[];
    },
    [extractFileKey, makeImageAttachmentFromFileRecord, shouldIncludeFileRecord]
  );

  const loadOverwriteImagesFromFiles = useCallback(
    async (files: OverwriteFileRecord[]) => {
      overwriteFilesRequestIdRef.current += 1;
      const requestId = overwriteFilesRequestIdRef.current;
      const attachments = await buildOverwriteImageAttachments(files);
      if (requestId !== overwriteFilesRequestIdRef.current) return;
      setOverwriteImages(attachments);
    },
    [buildOverwriteImageAttachments]
  );

  const addImageCandidatesForContent = useCallback(
    (candidates: ImageAddCandidate[]) => {
      const attachments = candidates.map(makeImageAttachment);
      if (isOverwrite) {
        setOverwriteImages((prev) => [...prev, ...attachments]);
      } else {
        setNewImages((prev) => [...prev, ...attachments]);
      }
    },
    [isOverwrite, makeImageAttachment]
  );

  const removeImageForContent = useCallback(
    (id: string) => {
      if (isOverwrite) {
        const removing = overwriteImagesRef.current.find((p) => p.id === id);
        if (removing && removing.file_id != null) {
          setOverwriteSavedImageRemoved(true);
        }
      }
      const setter = isOverwrite ? setOverwriteImages : setNewImages;
      setter((prev) => {
        const target = prev.find((p) => p.id === id);
        if (target) revokeImageAttachmentPreview(target);
        return prev.filter((p) => p.id !== id);
      });
    },
    [isOverwrite, revokeImageAttachmentPreview]
  );

  const resetImagesForFooter = useCallback(() => {
    if (isOverwrite) setOverwriteSavedImageRemoved(false);
    const setter = isOverwrite ? setOverwriteImages : setNewImages;
    setter((prev) => {
      prev.forEach(revokeImageAttachmentPreview);
      return [];
    });
  }, [isOverwrite, revokeImageAttachmentPreview]);

  const [createData, setCreateData] = useState<string>('');
  const [updateData, setUpdateData] = useState<string>('');

  const [modalPinned, setModalPinned] = useState<boolean>(false);
  const [modalPinnedWrId, setModalPinnedWrId] = useState<number | undefined>(
    undefined
  );

  // Overwrite old → 選択後に Content/Footer を表示するかどうか（単一の表示フラグ）
  const [overwriteEditorVisible, setOverwriteEditorVisible] =
    useState<boolean>(false);
  const overwriteSelectionCommittedRef = useRef<boolean>(false);
  // 選択直後に SearchArea が onSearchResults を再通知しても editor を閉じない（1回だけ無視）
  const ignoreNextOverwriteSearchResultsRef = useRef<boolean>(false);

  const [searchPerformed, setSearchPerformed] = useState<boolean>(false);
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [historyItems, setHistoryItems] = useState<HistoryItem[]>([]);

  useEffect(() => {
    if (!isOverwrite) {
      setOverwriteEditorVisible(false);
      overwriteSelectionCommittedRef.current = false;
      // overwrite 側の payload を破棄して完全に混入余地を消す
      searchSelectionRefOverwrite.current = null;

      setTitle('');
      setBody('');
      setFbNote('');
      setCreateData('');
      setUpdateData('');
      setValidationErrorsIfChanged({});
      setModalPinned(false);
      setModalPinnedWrId(undefined);
      setSearchResults([]);
      setSearchPerformed(false);
      setOverwriteTeamId(null);
      setTemporaryFlg(null);
      setOverwriteSavedImageRemoved(false);
      setOverwriteImages((prev) => {
        prev.forEach(revokeImageAttachmentPreview);
        return [];
      });
    }
  }, [activeTab, isOverwrite, revokeImageAttachmentPreview, setValidationErrorsIfChanged]);

  const handleLoadHistoryData = (item: HistoryItem) => {
    if (!isOverwrite) return;

    // 「選択確定（commit）」扱いにする（直後の modal close は無視する）
    overwriteSelectionCommittedRef.current = true;

    // 「選択直後に SearchArea が onSearchResults を再通知しても editor を閉じない（1回だけ）
    ignoreNextOverwriteSearchResultsRef.current = true;

    setOverwriteEditorVisible(true);

    setWrID(item.wr_id);
    setStartDate(item.start_date);
    setEndDate(item.end_date);
    setTitle(item.title || '');
    setBody(item.body || '');
    setFbNote(item.feedback_body || '');
    setCreateData(item.create_by || '');
    setUpdateData(item.update_by || '');
    setOverwriteTeamId(
      typeof item.team === 'number'
        ? item.team
        : item.team
        ? Number(item.team)
        : null
    );
    setTemporaryFlg(item.temporary_flg ?? 0);
    setOverwriteSavedImageRemoved(false);
    setOverwriteImages((prev) => {
      prev.forEach(revokeImageAttachmentPreview);
      return [];
    });
    const files = Array.isArray((item as any)?.files)
      ? ((item as any).files as OverwriteFileRecord[])
      : [];
    if (files.length > 0) {
      void loadOverwriteImagesFromFiles(files);
    }
    // 差し戻された下書きでは、課マスタの Enlight 同期デフォルト ON を再適用する。
    setEnlightSyncEnabled(
      resolveOverwriteEnlightSyncEnabled(
        item,
        divisionOptions,
        {
          fallbackDepartment: selectedDivision,
          remandPreference: readRemandEnlightSyncPreference(
            selfLoginId,
            item.wr_id
          ),
        }
      )
    );

    setModalPinned(true);
    setModalPinnedWrId(item.wr_id);
  };

  const handleHistoryLoaded = useCallback((items: HistoryItem[]) => {
    setHistoryItems(items);
  }, []);

  // New / Overwrite の動的フィールドを分離して保持する。
  // タブ切替直後でも、現在のタブとは別のスナップショットを登録処理に渡さない。
  const newDynamicFieldsRef = useRef<any[]>([]);
  const overwriteDynamicFieldsRef = useRef<any[]>([]);
  const [dynamicFieldsForValidation, setDynamicFieldsForValidation] = useState<any[]>([]);
  const attrsRef = useRef<
    Array<{ attr_id: number; attr_name: string; attr_value: string }>
  >([]);
  const [dynamicAttrsForValidation, setDynamicAttrsForValidation] = useState<
    Array<{ attr_id: number; attr_name: string; attr_value: string }>
  >([]);

  // 必須未入力のみ検証（文字数超過は含めない）
  const validateForm = useCallback((): boolean => {
    if (isOverwrite) {
      setValidationErrorsIfChanged({});
      return true;
    }

    const errors: ValidationErrors = {};
    if (!reporter.trim()) errors.reporter = '記入者は必須項目です';
    if (!startDate.trim()) errors.startDate = '開始日付は必須項目です';
    if (!draftTitle.trim()) errors.title = 'タイトルは必須項目です';
    if (!draftBody.trim()) errors.body = '本文は必須項目です';

    // 動的属性（weekly-input-items の input_required=1）の必須チェック
    const fields = Array.isArray(newDynamicFieldsRef.current)
      ? newDynamicFieldsRef.current
      : [];
    const missingDynamic = missingRequiredDynamicTypes(fields, attrsRef.current);
    if (missingDynamic.length > 0) {
      errors.dynamic = '必須の動的属性が入力されていません';
    }

  setValidationErrorsIfChanged(errors);
  return Object.keys(errors).length === 0;
}, [isOverwrite, reporter, startDate, draftTitle, draftBody, setValidationErrorsIfChanged]);

  useEffect(() => {
    validateForm();
  }, [
    reporter,
    startDate,
    title,
    body,
    draftTitle,
    draftBody,
    activeTab,
    validateForm,
  ]);

  const currentBody = isOverwrite ? body : draftBody;

  // Snackbar
  const [snackOpen, setSnackOpen] = useState<boolean>(false);
  const [snackMessage, setSnackMessage] = useState<string>('登録が完了しました');
  const handleSuccess = () => {
    // Footer 側のクリアに加え、登録成功を受けた親でも確実に初期化する。
    // Content 内の非制御 textarea は reset sequence による remount でDOM値も破棄する。
    setTitle('');
    setBody('');
    resetDraftTitle();
    resetDraftBody();
    setNewFormDefaultResetSeq((seq) => seq + 1);
    setSnackMessage('登録が完了しました');
    setSnackOpen(true);
  };
  const handleSnackbarClose = (_e: any, reason: SnackbarCloseReason) => {
    if (reason === 'clickaway') return;
    setSnackOpen(false);
  };
  const handleAlertClose = () => setSnackOpen(false);

  const handleSearchResults = useCallback(
    (results: any[]) => {
      setSearchPerformed(true);
      setSearchResults(results);

      if (activeTab === 'overwrite') {
        const isEmpty = !results || results.length === 0;

        // 選択直後の「再通知」は1回だけ無視（editor を閉じない）
        if (
          overwriteEditorVisible &&
          ignoreNextOverwriteSearchResultsRef.current
        ) {
          ignoreNextOverwriteSearchResultsRef.current = false;
          return;
        }

        // editor表示中に empty が来ても editor/pin を落とさない（既存の意図）
        if (overwriteEditorVisible && isEmpty) {
          return;
        }

        // editor表示中に non-empty が来た = ユーザーが「検索し直した」可能性が高い
        // → その場合だけ editor を閉じて検索結果に戻す
        if (overwriteEditorVisible && !isEmpty) {
          setOverwriteEditorVisible(false);
          setModalPinned(false);
          setModalPinnedWrId(undefined);
          overwriteSelectionCommittedRef.current = false;
          return;
        }

        // editor非表示中（検索一覧の表示状態）なら、pin解除だけしておく
        setModalPinned(false);
        setModalPinnedWrId(undefined);
      }
    },
    [activeTab, overwriteEditorVisible]
  );

  // モーダルで「閉じる」を押したら、Overwrite入力/Content/Footerを全て非表示＆状態リセット
  type OverwriteModalCloseReason = 'cancel' | 'commit';
  type OverwriteModalCloseOptions = { reason?: OverwriteModalCloseReason };

  const handleOverwriteModalClosed = useCallback(
    (opts?: OverwriteModalCloseOptions) => {
      if (!isOverwrite) return;

      const reason = opts?.reason;

      // 履歴選択による close は、編集画面へ遷移する正常ルートなので何もしない
      if (reason === 'commit') {
        overwriteSelectionCommittedRef.current = false;
        return;
      }

      // 後方互換用：
      // reason が来ない古い呼び出しでも、直前が選択確定なら commit 扱いで無視する
      if (!reason && overwriteSelectionCommittedRef.current) {
        overwriteSelectionCommittedRef.current = false;
        return;
      }

      // ×ボタン / 閉じるボタンは必ず cancel 扱いで、Footer/Content を白紙化する
      overwriteSelectionCommittedRef.current = false;
      ignoreNextOverwriteSearchResultsRef.current = false;

      // Overwrite の入力画面を確実に閉じる
      setOverwriteEditorVisible(false);

      // Overwrite 選択状態クリア
      setModalPinned(false);
      setModalPinnedWrId(undefined);

      // 入力値も安全側でクリア
      setWrID(undefined);
      setStartDate('');
      setEndDate('');
      setTitle('');
      setBody('');
      setFbNote('');
      setCreateData('');
      setUpdateData('');

      // 検索状態もクリア
      setSearchResults([]);
      setSearchPerformed(false);

      // 付随項目
      setOverwriteTeamId(null);
      setTemporaryFlg(null);

      // 添付画像もクリア
      setOverwriteImages((prev) => {
        prev.forEach(revokeImageAttachmentPreview);
        return [];
      });
    },
    [
      isOverwrite,
      setStartDate,
      setEndDate,
      revokeImageAttachmentPreview,
    ]
  );

  const hasOverwriteSelection =
    isOverwrite && (title.trim() !== '' || body.trim() !== '');
  const isFormValid: boolean = isOverwrite
    ? true
    : Object.keys(validationErrors).length === 0 &&
      !!reporter &&
      !!startDate &&
      !!draftTitle &&
      !!draftBody &&
      missingRequiredDynamicTypes(
        dynamicFieldsForValidation,
        dynamicAttrsForValidation
      ).length === 0;

  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    const s = location.state as {
      activeTab?: 'new' | 'overwrite';
      flash?: {
        type?: 'success' | 'error' | 'info' | 'warning';
        message?: string;
      };
    } | null;

    // setActiveTab 直叩きではなく handleTabChange を通す
    if (s?.activeTab) {
      handleTabChange(s.activeTab);
    }

    if (s?.flash?.message) {
      setSnackMessage(s.flash.message!);
      setSnackOpen(true);

      // flash は一度だけ表示する画面遷移情報なので、表示後に履歴から取り除く。
      // 残したままだとチーム変更などでこの effect が再実行された際に再表示される。
      const { flash: _consumedFlash, ...remainingState } = s;
      navigate(
        {
          pathname: location.pathname,
          search: location.search,
          hash: location.hash,
        },
        {
          replace: true,
          state: Object.keys(remainingState).length > 0 ? remainingState : null,
        }
      );
    }
  }, [
    location.hash,
    location.pathname,
    location.search,
    location.state,
    handleTabChange,
    navigate,
  ]);

  // DynamicFields / freeWords / attrs
  const onDynamicFieldsChange = useCallback(
    (fields: any[]) => {
      if (activeTab === 'overwrite') {
        overwriteDynamicFieldsRef.current = fields;
        return;
      }

      newDynamicFieldsRef.current = fields;
      setDynamicFieldsForValidation(fields);
      // 動的属性の入力でもボタン活性/非活性を更新したいため再検証
      validateForm();
    },
    [activeTab, validateForm]
  );
  const getDynamicFields = useCallback(
    (): any[] =>
      activeTab === 'overwrite'
        ? overwriteDynamicFieldsRef.current
        : newDynamicFieldsRef.current,
    [activeTab]
  );
  const getDynamicAttributes = useCallback(() => attrsRef.current, []);

  const freeWordsRef = useRef<Record<string, string>>({});
  const onFreeWordsChange = useCallback((fw: Record<string, string>) => {
    freeWordsRef.current = fw;
  }, []);
  const onAttributesChange = useCallback(
    (attrs: Array<{ attr_id: number; attr_name: string; attr_value: string }>) => {
      attrsRef.current = attrs;
      setDynamicAttrsForValidation(attrs);
      if (activeTab === 'new') validateForm();
    },
    [activeTab, validateForm]
  );

  const [resolvedDeptId, setResolvedDeptId] = useState<number | null>(null);
  const [resolvedTeamIdForNew, setResolvedTeamIdForNew] = useState<number | null>(
    null
  );
  const [resolvedTeamDocsetId, setResolvedTeamDocsetId] = useState<number | null | undefined>(undefined);

  const clearNewDatesRef = useRef<(() => void) | null>(null);

  // 本文最大文字数: チーム > 課 > 既定(MAX_BODY_LENGTH) の優先で決定
  // 注意: Number(null) は 0 になるため、そのまま数値化しない（null/undefined/'' は未設定扱いにする）
  const getNumberOrUndefined = (raw: any): number | undefined => {
    if (raw === null || raw === undefined || raw === '') return undefined;
    const n = Number(raw);
    return Number.isFinite(n) ? n : undefined;
  };

  // ============================================
  // DEPARTMENT_MANAGE.enlight_sync_default → Enlight同期のデフォルトに反映
  // ============================================
  // 選択中の課に対応する enlight_sync_default（DEPARTMENT_MANAGE）を取得
  const masterEnlightDefault = useMemo(() => {
    if (!Array.isArray(divisionOptions) || !divisionOptions.length) return null;

    const target = String(selectedDivision ?? '').trim();
    if (!target) return null;

    const normalize = (v: any) => String(v ?? '').trim();

    const hit = (divisionOptions as any[]).find((opt: any) => {
      const candList = [opt.department_id, opt.id, opt.value, opt.label].map(
        normalize
      );
      return candList.includes(target);
    });

    if (!hit) return null;

    return parseEnlightSyncDefault((hit as any).enlight_sync_default);
  }, [divisionOptions, selectedDivision]);

  // New tab defaults are applied once per dept/team/docset/new-form reset.
  const lastAppliedNewEnlightDefaultKeyRef = useRef<string | null>(null);

  useEffect(() => {
    const master = masterEnlightDefault;

    if (activeTab !== 'new') {
      lastAppliedNewEnlightDefaultKeyRef.current = null;
      return;
    }

    // マスタ側に値が無いときは何もしない
    if (master == null) return;

    // undefined means "not resolved yet"; wait so the loading disabled state
    // does not overwrite the department default. null means "no docset".
    if (resolvedTeamDocsetId === undefined) return;

    const applyKey = [
      selectedDivision || '',
      resolvedDeptId ?? '',
      resolvedTeamIdForNew ?? '',
      resolvedTeamDocsetId ?? 'none',
      master ? 1 : 0,
      newFormDefaultResetSeq,
    ].join('|');

    if (lastAppliedNewEnlightDefaultKeyRef.current === applyKey) return;
    lastAppliedNewEnlightDefaultKeyRef.current = applyKey;

    // Apply the department default only after the team's docset state is known.
    setEnlightSyncEnabled(resolvedTeamDocsetId == null ? false : master);
  }, [
    masterEnlightDefault,
    activeTab,
    selectedDivision,
    resolvedDeptId,
    resolvedTeamIdForNew,
    resolvedTeamDocsetId,
    newFormDefaultResetSeq,
  ]);

  // Enter の暗黙 submit を抑止
  const onKeyDownCapture = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key !== 'Enter') return;
      const t = e.target as HTMLElement | null;
      const tag = (t?.tagName || '').toLowerCase();
      const type = (t as HTMLInputElement)?.type?.toLowerCase?.() || '';
      const isTextLike =
        tag === 'input' && ['text', 'search', 'email', 'tel', 'url'].includes(type);
      const isInTM = !!t?.closest?.('.tm-card');
      const composing =
        (e as any).isComposing === true ||
        (e.nativeEvent as any)?.isComposing === true;
      if (!composing && (isTextLike || isInTM)) {
        e.preventDefault();
        e.stopPropagation();
      }
    },
    []
  );

  // SearchArea に渡す IDs 通知ハンドラを useCallback で固定
  const handleResolvedIdsChange = useCallback(
    ({
      departmentId,
      teamId,
    }: {
      departmentId: number | null;
      teamId: number | null;
    }) => {
      setResolvedDeptId(departmentId ?? null);

      // Newタブの値は Overwrite で壊さない（本文文字数上限や Footer などに使う）
      if (activeTab === 'new') {
        setResolvedTeamIdForNew(teamId ?? null);
      } else if (activeTab === 'overwrite') {
        setOverwriteTeamId(teamId ?? null);
      }
    },
    [activeTab]
  );


  // OverwriteInputSearchArea の表示ペイロードを Footer から読めるようにする
  // NEW / OVERWRITE で payload を分離して「連名者の混入」を防ぐ
  // 目安: handleResolvedIdsChange の直後あたり（元の searchSelectionRef 一式を置換）
  const searchSelectionRefNew = useRef<SearchSelectionPayload | null>(null);
  const searchSelectionRefOverwrite = useRef<SearchSelectionPayload | null>(null);

  // setState の非同期で「タブ切替直後に古い activeTab を参照する」事故を防ぐ
  const activeTabRef = useRef<'new' | 'overwrite'>(activeTab);

  useEffect(() => {
    activeTabRef.current = activeTab;
  }, [activeTab]);

  const handleSearchPayloadChange = useCallback(
    (p: SearchSelectionPayload | null) => {
      const tab = activeTabRef.current;

      if (tab === 'overwrite') {
        searchSelectionRefOverwrite.current = p;
      } else {
        searchSelectionRefNew.current = p;
      }
    },
    []
  );

  const getSearchSelectionPayload = useCallback(() => {
    if (activeTab !== 'overwrite') return null;
    // render 中は state を使ってOK（ref でも可）
    return activeTab === 'overwrite'
      ? searchSelectionRefOverwrite.current
      : searchSelectionRefNew.current;
  }, [activeTab]);

  // 動的: 本文最大文字数（チーム -> 課 -> 既定）の優先で決定
  const deptBodyMaxFromMaster = useMemo(() => {
    const id = Number(resolvedDeptId);
    if (!Number.isFinite(id) || id <= 0) return undefined;
    const found = (divisionOptions ?? []).find(
      (d: any) => Number((d as any)?.department_id) === id
    );
    const raw = (found as any)?.body_max_length;
    return getNumberOrUndefined(raw);
  }, [resolvedDeptId, divisionOptions]);

  const currentTeamId = isOverwrite ? overwriteTeamId : resolvedTeamIdForNew;
  const teamBodyMaxFromMaster = useMemo(() => {
    const tid = Number(currentTeamId);
    if (!Number.isFinite(tid) || tid <= 0) return undefined;
    const found = (teamOptions ?? []).find(
      (t: any) => Number((t as any)?.team_id) === tid
    );
    const raw = (found as any)?.body_max_length;
    return getNumberOrUndefined(raw);
  }, [currentTeamId, teamOptions]);

  const effectiveMaxBodyLength = useMemo(() => {
    if (typeof teamBodyMaxFromMaster === 'number' && teamBodyMaxFromMaster > 0) {
      return teamBodyMaxFromMaster;
    }
    if (typeof deptBodyMaxFromMaster === 'number' && deptBodyMaxFromMaster > 0) {
      return deptBodyMaxFromMaster;
    }
    return MAX_BODY_LENGTH;
  }, [teamBodyMaxFromMaster, deptBodyMaxFromMaster]);

  const remainingChars = effectiveMaxBodyLength - (currentBody?.length ?? 0);
  const getCharCountColor = (): 'hazard' | 'warning' | '' =>
    remainingChars <= 3 ? 'hazard' : remainingChars <= 10 ? 'warning' : '';

  const isEnlightSyncToggleDisabled = resolvedTeamDocsetId == null;
  const enlightSyncToggleDisabledReason =
    'ご利用のチームではEnlight同期が設定されていません。詳しくは管理者にご確認ください。';

  useEffect(() => {
    if (resolvedTeamDocsetId !== null) return;
    setEnlightSyncEnabled(false);
  }, [resolvedTeamDocsetId]);

  // ─────────────────────────────────────────────
  // WebSocket フォームロック（InputEdit 全体用）
  // ─────────────────────────────────────────────

  // 画面専用の WebSocket 表示名を作る（InputEdit 用）
  const baseUserName = useUserName();
  const wsUserName = useMemo(() => (baseUserName ? baseUserName : ''), [
    baseUserName,
  ]);

  const { sendJsonMessage, lastMessage, readyState } = useAppWebSocket();

  // 他ユーザー編集中の情報を wr_id ごとに保持（表示用には名前を使う）
  const [otherEditingByWrId, setOtherEditingByWrId] = useState<
    Record<number, string[]>
  >({});

  // 先着でロックを取得したユーザー（wr_id ごとのロックオーナー）
  const [lockOwnerByWrId, setLockOwnerByWrId] = useState<
    Record<number, { userId: string; name: string }>
  >({});

  // overwrite + モーダル固定時だけロック対象とする
  const effectiveLockWrId = useMemo<number | null>(() => {
    if (!isOverwrite) return null;
    if (!modalPinned) return null;
    if (typeof wrId !== 'number') return null;
    return wrId;
  }, [isOverwrite, modalPinned, wrId]);

  // 「今この画面で見ている wr_id」に対して、ロックオーナーが自分以外ならロック
  const lockedByName = useMemo<string | null>(() => {
    if (effectiveLockWrId == null) return null;

    const owner = lockOwnerByWrId[effectiveLockWrId];
    if (owner) {
      const ownerUserId = owner.userId || '';
      const ownerName = owner.name || '';

      const isOwnerSelfById =
        !!selfLoginId && !!ownerUserId && selfLoginId === ownerUserId;
      const isOwnerSelfByName =
        !!wsUserName && !!ownerName && wsUserName === ownerName;

      // 自分がオーナーならロックしない
      if (isOwnerSelfById || isOwnerSelfByName) {
        return null;
      }

      // 他人がオーナーなら、その人の名前を表示
      return ownerName || null;
    }

    // オーナー情報が無い場合は、従来どおり「他ユーザー一覧」から決定
    const others = otherEditingByWrId[effectiveLockWrId] ?? [];
    if (others.length === 0) return null;
    return others[0] ?? null;
  }, [
    effectiveLockWrId,
    lockOwnerByWrId,
    otherEditingByWrId,
    selfLoginId,
    wsUserName,
  ]);

  const isLocked = !!lockedByName;

  // 下書き編集中のみ、対象 wr_id に対して Using / Release を送信
  useEffect(() => {
    if (effectiveLockWrId == null) {
      return;
    }
    if (readyState !== ReadyState.OPEN) {
      return;
    }

    try {
      sendStatusUsing(
        sendJsonMessage,
        effectiveLockWrId,
        (user as any)?.signInDetails?.loginId,
        wsUserName
      );
    } catch (e) {
      console.error('InputEdit Using send failed', e);
    }

    return () => {
      try {
        sendStatusRelease(
          sendJsonMessage,
          effectiveLockWrId,
          (user as any)?.signInDetails?.loginId,
          wsUserName
        );
      } catch (e) {
        console.error('InputEdit Release send failed', e);
      }
    };
  // }, [effectiveLockWrId, readyState, sendJsonMessage, user, wsUserName]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveLockWrId, readyState]);

  // ハートビート: TTL 切れ防止のため 30 分ごとにロックを更新
  useEffect(() => {
    if (effectiveLockWrId == null) return;
    if (readyState !== ReadyState.OPEN) return;

    const timer = setInterval(() => {
      try {
        sendStatusUsing(
          sendJsonMessage,
          effectiveLockWrId,
          (user as any)?.signInDetails?.loginId,
          wsUserName
        );
      } catch (e) {
        console.error('InputEdit heartbeat send failed', e);
      }
    }, 30 * 60 * 1000);

    return () => clearInterval(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [effectiveLockWrId, readyState, sendJsonMessage, user, wsUserName]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveLockWrId, readyState]);

  // WebSocket メッセージから「今編集中の wr_id」とロックオーナーを監視してロック状態を更新
  useEffect(() => {
    if (!lastMessage) return;

    try {
      const raw = lastMessage.data as any;
      const msg = typeof raw === 'string' ? JSON.parse(raw) : raw;

      // event / data ラッパーを考慮した payload
      let payload: any = msg;
      if (
        payload &&
        typeof payload === 'object' &&
        !Array.isArray(payload) &&
        (payload as any).data
      ) {
        payload = (payload as any).data;
      }

      const getId = (v: any): number => {
        return Number(v?.wr_id ?? v?.wrid ?? v?.id);
      };

      const getName = (v: any): string => {
        // まず username / name を優先
        const n = String(v?.username ?? v?.name ?? '').trim();
        if (n) return n;
        // 無ければ userid / user（＝ログインID）をフォールバックとして使う
        const uid = String(v?.userid ?? v?.user ?? '').trim();
        return uid;
      };

      const getUserId = (v: any): string => {
        return String(v?.userid ?? v?.user ?? '').trim();
      };

      const isSelfUser = (v: any): boolean => {
        const uid = getUserId(v);
        const name = getName(v);

        // まず userid / user と ログインID で判定
        if (selfLoginId && uid) {
          if (uid === selfLoginId) return true;
        }

        // フォールバックとして氏名での比較
        if (wsUserName && name) {
          if (name === wsUserName) return true;
        }

        return false;
      };

      // 新メッセージタイプ対応
      if (payload && typeof payload === 'object' && !Array.isArray(payload)) {
        if (payload.type === 'connected') {
          return; // connectionId の保存は WebSocketContext で処理
        }
        if (payload.type === 'lock_expired') {
          const expiredWrId = Number(payload.wr_id);
          if (expiredWrId && effectiveLockWrId && expiredWrId === effectiveLockWrId) {
            setSnackMessage('編集ロックの有効期限が切れました。ページを操作すると再取得されます。');
            setSnackOpen(true);
          }
          return;
        }
      }

      // 配列形式（presence 一括など）
      if (Array.isArray(payload)) {
        // 他ユーザー一覧（自分は除外）
        const nextOthers: Record<number, string[]> = {};

        payload.forEach((item: any) => {
          if (!item) return;

          const id = getId(item);
          const name = getName(item);
          if (!id || !name) return;

          // 自分自身は「他人の編集中リスト」に入れない
          if (isSelfUser(item)) return;

          if (!nextOthers[id]) nextOthers[id] = [];
          if (!nextOthers[id].includes(name)) {
            nextOthers[id].push(name);
          }
        });

        setOtherEditingByWrId(nextOthers);

        // ロックオーナーも、スナップショットに合わせて整合させる
        setLockOwnerByWrId((prev) => {
          const next = { ...prev };

          // 今回のスナップショットに出てきた wr_id 一覧
          const presentIds = new Set<number>();
          payload.forEach((item: any) => {
            const id = getId(item);
            if (!id) return;
            presentIds.add(id);
          });

          // 1) スナップショットに存在しない wr_id のオーナーは削除
          Object.keys(next).forEach((key) => {
            const id = Number(key);
            if (!presentIds.has(id)) {
              delete next[id];
            }
          });

          // 2) スナップショットに存在するが、オーナーが snapshot に含まれていない場合は、先頭要素をオーナーにする
          payload.forEach((item: any) => {
            const id = getId(item);
            if (!id) return;

            const uid = getUserId(item);
            const name = getName(item);
            if (!uid && !name) return;

            const currentOwner = next[id];

            if (!currentOwner) {
              // まだオーナーがいない wr_id は、最初に見つけたユーザーをオーナーにする
              next[id] = { userId: uid, name };
              return;
            }

            const ownerStillExists = payload.some((p: any) => {
              const pid = getId(p);
              const puid = getUserId(p);
              return pid === id && puid === currentOwner.userId;
            });

            if (!ownerStillExists) {
              // 既存オーナーが snapshot から消えている場合は、先頭のユーザーを新しいオーナーにする
              const first = payload.find((p: any) => getId(p) === id);
              if (first) {
                next[id] = {
                  userId: getUserId(first),
                  name: getName(first),
                };
              } else {
                delete next[id];
              }
            }
          });

          return next;
        });

        return;
      }

      // presence 形式
      if (payload && typeof payload === 'object') {
        if (
          payload.type === 'presence' &&
          payload.wr_id != null &&
          Array.isArray(payload.users)
        ) {
          const id = Number(payload.wr_id);

          // users 配列の中身の形式はサーバー依存なので、
          // 文字列（名前 or ログインID）前提で扱う
          const others = (payload.users as any[])
            .map((u) => String(u ?? '').trim())
            .filter((val) => {
              if (!val) return false;
              if (wsUserName && val === wsUserName) return false;
              if (selfLoginId && val === selfLoginId) return false;
              return true;
            });

          setOtherEditingByWrId((prev) => {
            const next = { ...prev };
            if (others.length > 0) {
              next[id] = others;
            } else {
              delete next[id];
            }
            return next;
          });

          // presence は一覧情報なので、オーナー整合は snapshot 側で行う想定
          return;
        }

        const id = getId(payload);
        const name = getName(payload);
        const status = Number(payload.status ?? 0);
        const uid = getUserId(payload);

        if (!id || (!uid && !name)) return;

        // 自分自身の Using / Release は「他人のリスト」には反映しない
        if (!isSelfUser(payload)) {
          setOtherEditingByWrId((prev) => {
            const next = { ...prev };
            const arr = next[id] ? [...next[id]] : [];

            if (status === WeeklyReportStatus.Release) {
              // Release の場合は該当ユーザーを削除
              const filtered = arr.filter((n) => n !== name);
              if (filtered.length > 0) {
                next[id] = filtered;
              } else {
                delete next[id];
              }
            } else {
              // Using などの場合は「他ユーザー編集中」に追加
              if (!arr.includes(name)) {
                arr.push(name);
              }
              next[id] = arr;
            }

            return next;
          });
        }

        // ロックオーナーの更新：
        //  - Using 系(status !== Release) で、まだオーナーがいなければその人をオーナーにする
        //  - Release で、オーナー本人ならオーナーを削除
        setLockOwnerByWrId((prev) => {
          const next = { ...prev };
          const owner = next[id];

          if (status === WeeklyReportStatus.Release) {
            if (owner) {
              const isSameById = !!uid && !!owner.userId && uid === owner.userId;
              const isSameByName = !!name && !!owner.name && name === owner.name;

              if (isSameById || isSameByName) {
                delete next[id];
              }
            }
          } else {
            if (!owner) {
              next[id] = { userId: uid, name };
            }
          }

          return next;
        });
      }
    } catch (e) {
      console.error('InputEdit WebSocket parse error', e);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastMessage, wsUserName, selfLoginId]);

  // ─────────────────────────────────────────────
  // ロック解除時に一度だけ WEEKLY_REPORT_BASE を最新化する
  // 週報連の API（weekly-report-search）を利用
  // ─────────────────────────────────────────────
  const prevIsLockedRef = useRef<boolean>(false);
  const lastReloadedWrIdRef = useRef<number | null>(null);

  useEffect(() => {
    // ロック中に入ったら、次回 unlock 時の再取得を必ず許可する
    if (isLocked && effectiveLockWrId != null) {
      lastReloadedWrIdRef.current = null;
    }
  }, [isLocked, effectiveLockWrId]);

  useEffect(() => {
    // ロック状態の変化を検知
    const prev = prevIsLockedRef.current;
    prevIsLockedRef.current = isLocked;

    // ロック中 → ロック解除 の時だけ実行
    if (prev && !isLocked) {
      const targetWrId = effectiveLockWrId;
      if (!targetWrId) return;

      // 同じ wr_id に対しては二重実行しない
      if (lastReloadedWrIdRef.current === targetWrId) {
        return;
      }

      const fetchLatest = async () => {
        try {
          const params = new URLSearchParams();

          // start_date / end_date は、現在フォームが持っている値をそのまま使用
          if (startDate) {
            params.set('start_date', startDate);
          }
          if (endDate) {
            params.set('end_date', endDate);
          }

          // departments は resolvedDeptId があればそれを優先、なければ selectedDivision をそのまま投げる
          if (resolvedDeptId != null) {
            params.set('departments', String(resolvedDeptId));
          } else if (selectedDivision) {
            params.set('departments', String(selectedDivision));
          }

          // USER_DEP: ユーザの課を付与（division -> department -> 選択中division の順でフォールバック）
          {
            const userDep = String(
              (profile as any)?.division ??
                profile?.department ??
                selectedDivision ??
                ''
            ).trim();
            if (userDep) {
              params.set('USER_DEP', userDep);
            }
          }

          // URL に USER_DEP を含めない。API の params 経由で付与して送信する
          const res = await apiClient.get('/weekly-report-search', { params });
          const list: any[] = Array.isArray(res.data) ? res.data : [];

          // 対象 wr_id のレコードを特定
          const hit = list.find(
            (item) => Number(item?.wr_id) === Number(targetWrId)
          );
          if (!hit) {
            console.warn(
              'weekly-report-search: target wr_id not found after unlock',
              { targetWrId, listLength: list.length }
            );
            return;
          }

          // ここで InputEdit のフォーム状態を最新に更新
          setWrID(hit.wr_id);
          setTitle(hit.title ?? '');
          setBody(hit.body ?? '');
          setFbNote(hit.feedback_body ?? '');

          setStartDate(hit.start_date ?? '');
          setEndDate(hit.end_date ?? '');

          setCreateData(hit.create_by ?? '');
          setUpdateData(hit.update_by ?? '');

          setOverwriteTeamId(
            typeof hit.team === 'number'
              ? hit.team
              : hit.team
              ? Number(hit.team)
              : null
          );
          setTemporaryFlg(
            typeof hit.temporary_flg === 'number'
              ? hit.temporary_flg
              : hit.temporary_flg
              ? Number(hit.temporary_flg)
              : 0
          );

          // 再読込時も、差し戻された下書きには課マスタのデフォルト ON を再適用する。
          setEnlightSyncEnabled(
            resolveOverwriteEnlightSyncEnabled(
              hit,
              divisionOptions,
              {
                fallbackDepartment: selectedDivision,
                remandPreference: readRemandEnlightSyncPreference(
                  selfLoginId,
                  hit.wr_id
                ),
              }
            )
          );

          setOverwriteSavedImageRemoved(false);
          setOverwriteImages((prev) => {
            prev.forEach(revokeImageAttachmentPreview);
            return [];
          });
          const files = Array.isArray((hit as any)?.files)
            ? ((hit as any).files as OverwriteFileRecord[])
            : [];
          if (files.length > 0) {
            void loadOverwriteImagesFromFiles(files);
          }

          // 二重で呼び出さないように記録
          lastReloadedWrIdRef.current = targetWrId;
        } catch (e: any) {
          if (isApiError?.(e)) {
            console.error('weekly-report-search reload failed (api error)', e);
          } else {
            console.error('weekly-report-search reload failed', e);
          }
        }
      };

      // 非同期で実行
      fetchLatest();
    }
  }, [
    isLocked,
    effectiveLockWrId,
    startDate,
    endDate,
    resolvedDeptId,
    selectedDivision,
    divisionOptions,
    selfLoginId,
    profile,
    setStartDate,
    setEndDate,
    loadOverwriteImagesFromFiles,
    revokeImageAttachmentPreview,
  ]);

  const [resolvedCreateBy, setResolvedCreateBy] = useState('');
  const isEmailLike = (s: string) => /\S+@\S+\.\S+/.test(s);

  const effectiveReporter = useMemo(() => {
    const r = reporter.trim();
    if (!r) return '';
    if (isEmailLike(r)) return resolvedCreateBy.trim() || r;
    return r; // 氏名なら reporter を正にする
  }, [reporter, resolvedCreateBy]);

  // Overwrite old → 選択後に Content/Footer を表示するかどうか（単一の表示フラグ）
  const isOverwriteEditing = useMemo(() => {
    return isOverwrite && overwriteEditorVisible;
  }, [isOverwrite, overwriteEditorVisible]);

  // Overwrite old → input で title/body が複数表示される対策：
  // Content に渡す一覧データを「選択中 wr_id のみ」に絞る
  const contentHistoryItems = useMemo<HistoryItem[]>(() => {
    if (!isOverwriteEditing) return historyItems;
    if (typeof wrId !== 'number') return [];
    return historyItems.filter((h) => Number(h.wr_id) === Number(wrId));
  }, [isOverwriteEditing, historyItems, wrId]);

  const contentSearchResults = useMemo<any[]>(() => {
    if (!isOverwriteEditing) return searchResults;
    return [];
  }, [isOverwriteEditing, searchResults]);

  const contentSearchPerformed = useMemo<boolean>(() => {
    if (!isOverwriteEditing) return searchPerformed;
    return false;
  }, [isOverwriteEditing, searchPerformed]);

  const shouldShowEditor = !isOverwrite || overwriteEditorVisible;

  return (
    <div onKeyDownCapture={onKeyDownCapture}>
      <Snackbar
        open={snackOpen}
        autoHideDuration={1500}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={handleAlertClose}
          severity="success"
          variant="filled"
          sx={{
            width: '270px',
            height: '60px',
            backgroundColor: '#04b48bff',
            '& .MuiAlert-icon': { color: '#ffffff', fontSize: '1.7rem' },
          }}
        >
          {snackMessage}
        </Alert>
      </Snackbar>

      <Tabs />

      <div className="report-form-container">
        <SearchArea
          key={searchAreaMountKey}
          activeTab={activeTab}
          setActiveTab={handleTabChange}
          divisionOptions={divisionOptions}
          division={selectedDivision}
          setDivision={setSelectedDivision}
          team={team}
          setTeam={setTeam}
          reportType={reportType}
          setReportType={setReportType}
          approver3={approver3}
          setApprover3={setApprover3}
          approverAM={approverAM}
          setApproverAM={setApproverAM}
          reporter={reporter}
          setReporter={setReporter}
          startDate={startDate}
          setStartDate={setStartDate}
          endDate={endDate}
          setEndDate={setEndDate}
          shikimuke={shikimuke}
          setShikimuke={setShikimuke}
          lot={lot}
          setLot={setLot}
          enlightSyncEnabled={enlightSyncEnabled}
          onLoadHistoryData={handleLoadHistoryData}
          onSearchResults={handleSearchResults}
          onHistoryLoaded={handleHistoryLoaded}
          onDynamicFieldsChange={onDynamicFieldsChange}
          onFreeWordsChange={onFreeWordsChange}
          onAttributesChange={onAttributesChange}
          validationErrors={validationErrors}
          onSearchPayloadChange={handleSearchPayloadChange}
          onResolvedIdsChange={handleResolvedIdsChange}
          onResolvedTeamDocsetIdChange={setResolvedTeamDocsetId}
          // モーダルで「閉じる」を押したら、Overwrite入力/Content/Footerを全て非表示＆状態リセット
          onOverwriteModalClosed={handleOverwriteModalClosed}
          isLocked={isLocked}
          lockedByName={lockedByName}
          loginId={selfLoginId}
          onProvideClearNewDates={(fn) => {
            clearNewDatesRef.current = fn;
          }}
          onResolvedCreateByChange={setResolvedCreateBy}
        />

        {/* ここから下を「編集中は操作不可」 */}
        <div
          className="search-area-lock-container"
          style={
            isLocked
              ? {
                  pointerEvents: 'none',
                  opacity: 0.4,
                  filter: 'grayscale(0.2)',
                }
              : undefined
          }
        >
          {shouldShowEditor && (
            <Content
              key={`input-content-${newFormDefaultResetSeq}`}
              activeTab={activeTab}
              division={selectedDivision}
              draftTitle={draftTitle}
              setDraftTitle={setDraftTitle}
              draftBody={draftBody}
              setDraftBody={setDraftBody}
              title={title}
              setTitle={setTitle}
              body={body}
              setBody={setBody}
              fbNote={fbNote}
              setFbNote={setFbNote}
              enlightSyncEnabled={enlightSyncEnabled}
              autoSaveEnabled={autoSaveEnabled}
              searchResults={contentSearchResults}
              searchPerformed={contentSearchPerformed}
              validationErrors={validationErrors}
              historyItems={contentHistoryItems}
              setWrId={setWrID}
              setStartDate={setStartDate}
              setEndDate={setEndDate}
              setReporter={setReporter}
              modalPinned={modalPinned}
              modalPinnedWrId={modalPinnedWrId}
              remainingChars={remainingChars}
              charCountColor={getCharCountColor()}
              images={isOverwrite ? overwriteImages : newImages}
              onAddImageCandidates={addImageCandidatesForContent}
              onRemoveImage={removeImageForContent}
              footerSlot={
                <Footer
                  activeTab={activeTab}
                  searchResults={searchResults}
                  division={selectedDivision}
                  reporter={reporter}
                  startDate={startDate}
                  endDate={endDate}
                  wrId={wrId}
                  approverAM={approverAM}
                  approver3={approver3}
                  draftTitle={draftTitle}
                  draftBody={draftBody}
                  title={title}
                  setTitle={setTitle}
                  body={body}
                  setBody={setBody}
                  enlightSyncEnabled={enlightSyncEnabled}
                  setEnlightSyncEnabled={setEnlightSyncEnabled}
                  autoSaveEnabled={autoSaveEnabled}
                  setAutoSaveEnabled={setAutoSaveEnabled}
                  resetDraftTitle={resetDraftTitle}
                  resetDraftBody={resetDraftBody}
                  onSuccess={handleSuccess}
                  hasOverwriteSelection={hasOverwriteSelection}
                  isFormValid={isFormValid}
                  validateForm={validateForm}
                  validationErrors={validationErrors}
                  createBy={isOverwrite ? createData : effectiveReporter}
                  updateBy={updateData}
                  modalPinned={modalPinned}
                  getDynamicFields={getDynamicFields}
                  getDynamicAttributes={getDynamicAttributes}
                  getSearchSelectionPayload={getSearchSelectionPayload}
                  team={team}
                  overwriteTeamId={overwriteTeamId}
                  resolvedDeptId={resolvedDeptId}
                  resolvedTeamIdForNew={resolvedTeamIdForNew}
                  remainingChars={remainingChars}
                  temporaryFlg={temporaryFlg}
                  fbNote={fbNote}
                  loginId={selfLoginId}
                  images={isOverwrite ? overwriteImages : newImages}
                  onResetImages={resetImagesForFooter}
                  savedImageRemoved={isOverwrite && overwriteSavedImageRemoved}
                  setStartDate={setStartDate}
                  setEndDate={setEndDate}
                  setSearchSelectionPayload={handleSearchPayloadChange}
                  onClearNewDates={() => {
                    clearNewDatesRef.current?.();
                  }}
                  enlightSyncToggleDisabled={isEnlightSyncToggleDisabled}
                  enlightSyncToggleDisabledReason={enlightSyncToggleDisabledReason}
                />
              }
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default InputEdit;
